/**
 * Events — upcoming/past tabs, DateBlock rows, FAB → create.
 * Wired: GET /api/events. Port of EventsScreen in
 * design/source/screens-poll-event.jsx.
 */
import { router } from 'expo-router';
import React, { useState } from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/components/gate/icons';
import { CardSkeletons, CatPill, DateBlock, Fab, goBack, SubTabs } from '@/features/community/bits';
import { eventCatLabel, fetchEvents, useFetch, type SocietyEvent } from '@/features/community/lib';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Card, EmptyState, Screen, TopBar } from '@/ui';

export default function EventsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
  const { data, loading, error, refetch } = useFetch(fetchEvents, []);

  const list = (data ?? []).filter((e) => (tab === 'upcoming' ? !e.past : e.past));

  return (
    <View style={{ flex: 1, backgroundColor: colors.canvas0 }}>
      <TopBar title={t('ct_tab_events')} onBack={goBack} />
      <SubTabs tabs={[['upcoming', t('ct_upcoming')], ['past', t('ct_past')]] as const} value={tab} onChange={setTab} />
      <Screen padded style={{ paddingTop: space.s4 }}>
        {loading ? (
          <CardSkeletons />
        ) : error ? (
          <EmptyState
            icon={<Icon name="calendar" size={40} color={colors.textTertiary} />}
            title={t('ct_events_err_title')}
            body={t('event_err_body')}
            cta={t('retry')}
            onCta={() => void refetch()}
          />
        ) : list.length === 0 ? (
          <EmptyState
            icon={<Icon name="calendar" size={40} color={colors.textTertiary} />}
            title={tab === 'upcoming' ? t('event_empty_upcoming_title') : t('event_empty_past_title')}
            body={
              tab === 'upcoming'
                ? t('event_empty_upcoming_body')
                : t('event_empty_past_body')
            }
          />
        ) : (
          <View style={{ gap: space.s3 }}>
            {list.map((e) => <EventRow key={e.id} event={e} />)}
          </View>
        )}
      </Screen>
      <Fab label={t('event_create')} onPress={() => router.push('/events/create')} />
    </View>
  );
}

function EventRow({ event }: { event: SocietyEvent }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <Card onPress={() => router.push(`/events/${event.id}`)} style={{ flexDirection: 'row', gap: space.s3 }}>
      <DateBlock day={event.day} month={event.month} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <CatPill label={eventCatLabel(event.category, t)} />
        <AppText variant="headingSm" weight="500" style={{ marginTop: space.s2 }}>{event.title}</AppText>
        {event.venue ? (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <Icon name="map-pin" size={13} color={colors.textTertiary} />
            <AppText variant="bodySm" tone="tertiary">{event.venue}</AppText>
          </View>
        ) : null}
        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 4 }}>{t('event_going', { n: event.going })}</AppText>
      </View>
    </Card>
  );
}
